import { useState } from 'react'
import {
  VStack,
  Box,
  Text,
  Button,
  Input,
  List,
  ListItem,
  IconButton,
  HStack,
  useToast
} from '@chakra-ui/react'
import { DeleteIcon, AttachmentIcon } from '@chakra-ui/icons'

interface GeometryFile {
  id: string
  name: string
  size: number
  type: string
  uploadDate: Date
}

export const GeometryConfig = () => {
  const [files, setFiles] = useState<GeometryFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const toast = useToast()

  const acceptedFileTypes = '.stl,.step,.iges,.stp,.igs'

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (!selectedFiles?.length) return

    setIsUploading(true)
    try {
      // Process each selected file
      for (const file of selectedFiles) {
        // Validate file type
        const fileExtension = file.name.split('.').pop()?.toLowerCase()
        if (!fileExtension || !acceptedFileTypes.includes(fileExtension)) {
          toast({
            title: 'Invalid file type',
            description: `${file.name} is not a supported geometry file`,
            status: 'error',
            duration: 5000,
          })
          continue
        }

        // Create new geometry file entry
        const newFile: GeometryFile = {
          id: Date.now().toString(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date()
        }

        // TODO: Implement actual file upload to cloud storage
        await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate upload

        setFiles(prev => [...prev, newFile])
        
        toast({
          title: 'File uploaded successfully',
          description: file.name,
          status: 'success',
          duration: 3000,
        })
      }
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error?.message || 'Failed to upload file',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = (id: string) => {
    // TODO: Implement deletion from cloud storage
    setFiles(prev => prev.filter(file => file.id !== id))
    toast({
      title: 'File deleted',
      status: 'info',
      duration: 3000,
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <VStack spacing={6} align="start" width="100%" maxW="800px" mx="auto" p={4}>
      <Box width="100%">
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Upload Geometry Files
        </Text>
        
        <Input
          type="file"
          accept={acceptedFileTypes}
          onChange={handleFileSelect}
          display="none"
          id="geometry-file-input"
          multiple
        />
        <Button
          as="label"
          htmlFor="geometry-file-input"
          colorScheme="blue"
          leftIcon={<AttachmentIcon />}
          isLoading={isUploading}
          cursor="pointer"
        >
          Select Files
        </Button>
      </Box>

      <Box width="100%">
        <Text fontSize="md" fontWeight="bold" mb={2}>
          Uploaded Files
        </Text>
        <List spacing={2}>
          {files.map(file => (
            <ListItem
              key={file.id}
              p={3}
              bg="gray.50"
              borderRadius="md"
            >
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">{file.name}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {formatFileSize(file.size)} • {file.uploadDate.toLocaleDateString()}
                  </Text>
                </VStack>
                <IconButton
                  aria-label="Delete file"
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleDelete(file.id)}
                />
              </HStack>
            </ListItem>
          ))}
        </List>
        {files.length === 0 && (
          <Text color="gray.500">No files uploaded yet</Text>
        )}
      </Box>
    </VStack>
  )
}
